// --- FILE: src/hooks/useIdleTimer.js ---
import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook personalizado para detectar inactividad del usuario y manejar auto-logout
 * @param {Object} options - Configuración del timer
 * @param {number} options.timeout - Tiempo de inactividad en ms antes del warning (default: 60000 = 1 min)
 * @param {number} options.warningTime - Tiempo de warning en ms antes del logout (default: 20000 = 20 seg)
 * @param {Function} options.onIdle - Callback cuando el usuario está inactivo (muestra warning)
 * @param {Function} options.onTimeout - Callback cuando se agota el tiempo (ejecuta logout)
 * @param {boolean} options.enabled - Si el timer está activo (default: true)
 * @returns {Object} Estado y funciones del timer
 */
export const useIdleTimer = ({
  timeout = 10000, // 10 segundos por defecto
  warningTime = 0, // Sin warning por defecto
  onIdle = () => {},
  onTimeout = () => {},
  enabled = true
} = {}) => {
  
  // Estados del hook
  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Referencias para los timers
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Eventos optimizados - removido mousemove para mejor rendimiento
  const events = [
    'mousedown',
    'keypress',
    'keydown',
    'scroll',
    'touchstart',
    'click'
  ];
  
  // Función para limpiar todos los timers
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);
  
  // Función para iniciar el countdown del warning
  const startCountdown = useCallback(() => {
    let timeLeft = warningTime;
    setRemainingTime(Math.ceil(timeLeft / 1000));
    
    countdownTimerRef.current = setInterval(() => {
      timeLeft -= 1000;
      const secondsLeft = Math.ceil(timeLeft / 1000);
      setRemainingTime(secondsLeft);
      
      if (timeLeft <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        
        // Ejecutar logout automático
        setIsWarning(false);
        setIsIdle(false);
        onTimeout();
      }
    }, 1000);
  }, [warningTime, onTimeout]);
  
  // Función que se ejecuta cuando se detecta inactividad
  const handleIdle = useCallback(() => {
    if (!enabled) return;
    
    setIsIdle(true);
    setIsWarning(true);
    onIdle();
    
    // Iniciar countdown para logout automático
    startCountdown();
    
    // Timer para logout automático después del warning
    warningTimerRef.current = setTimeout(() => {
      setIsWarning(false);
      setIsIdle(false);
      onTimeout();
    }, warningTime);
    
  }, [enabled, onIdle, onTimeout, warningTime, startCountdown]);
  
  // Función para reiniciar el timer de inactividad
  const resetTimer = useCallback(() => {
    if (!enabled) return;
    
    // Limpiar timers existentes
    clearAllTimers();
    
    // Resetear estados
    setIsIdle(false);
    setIsWarning(false);
    setRemainingTime(0);
    
    // Actualizar timestamp de última actividad
    lastActivityRef.current = Date.now();
    
    // Iniciar nuevo timer de inactividad
    idleTimerRef.current = setTimeout(handleIdle, timeout);
    
  }, [enabled, timeout, handleIdle, clearAllTimers]);
  
  // Manejador de eventos de actividad - mejorado throttling
  const handleActivity = useCallback((event) => {
    // Throttling más agresivo para mejor rendimiento
    const now = Date.now();
    if (now - lastActivityRef.current < 2000) {
      return; // Ignorar eventos muy frecuentes (menos de 2 segundos)
    }
    
    lastActivityRef.current = now;
    resetTimer();
  }, [resetTimer]);
  
  // Función pública para pausar el timer
  const pause = useCallback(() => {
    clearAllTimers();
    setIsIdle(false);
    setIsWarning(false);
    setRemainingTime(0);
  }, [clearAllTimers]);
  
  // Función pública para reanudar el timer
  const resume = useCallback(() => {
    if (enabled) {
      resetTimer();
    }
  }, [enabled, resetTimer]);
  
  // Función pública para reiniciar manualmente
  const reset = useCallback(() => {
    resetTimer();
  }, [resetTimer]);
  
  // Función para cerrar el warning manualmente
  const dismissWarning = useCallback(() => {
    clearAllTimers();
    setIsWarning(false);
    setIsIdle(false);
    setRemainingTime(0);
    resetTimer();
  }, [clearAllTimers, resetTimer]);
  
  // Effect para configurar y limpiar event listeners
  useEffect(() => {
    if (!enabled) {
      // Si está deshabilitado, limpiar todo
      clearAllTimers();
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      return;
    }
    
    // Agregar event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { 
        passive: true,
        capture: true 
      });
    });
    
    // Iniciar timer inicial
    resetTimer();
    
    // Cleanup function
    return () => {
      clearAllTimers();
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, handleActivity, resetTimer, clearAllTimers]);
  
  // Effect para limpiar timers al desmontar el componente
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);
  
  return {
    // Estados
    isIdle,
    isWarning,
    remainingTime,
    
    // Funciones de control
    pause,
    resume,
    reset,
    dismissWarning,
    
    // Información útil
    timeoutDuration: timeout,
    warningDuration: warningTime,
    isEnabled: enabled,
    lastActivity: lastActivityRef.current
  };
};

export default useIdleTimer;